import type { ColumnDef } from '@tanstack/react-table';

import { Skeleton } from '@connected/ui/components/skeleton';
import { cn } from '@connected/ui/lib/utils';
import { useNavigate } from '@tanstack/react-router';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import { DataTable } from '@/components/data-table';

import type { FinalCircuit } from '../utils/final-circuit-list-types';

import { FinalCircuitActions } from './final-circuit-actions';

export type FinalCircuitTableProps = {
    electricalProjectVersionId: string;
    finalCircuits: FinalCircuit[];
    isLoading: boolean;
    className?: string;
    search?: Record<string, unknown>;
};

function formatDescription(finalCircuit: FinalCircuit): string | null {
    const parts = [finalCircuit.FloorNumber, finalCircuit.SpaceNumber].filter(Boolean);
    if (parts.length === 0)
        return null;
    return parts.join(' – ');
}

export function FinalCircuitTable({
    electricalProjectVersionId,
    finalCircuits,
    isLoading,
    className,
    search,
}: FinalCircuitTableProps) {
    const { t } = useTranslation('re-inspections');
    const navigate = useNavigate();

    const columns = useMemo<ColumnDef<FinalCircuit>[]>(() => ([
        {
            accessorKey: 'Name',
            header: 'Naam',
            cell: ({ row }) => {
                const description = formatDescription(row.original);
                return (
                    <div className="flex flex-col gap-0.5">
                        <span className="font-semibold">
                            {row.original.Identifier || 'Naamloze achterliggende installatie'}
                        </span>
                        {description && (
                            <span className="text-muted-foreground text-sm">
                                {description}
                            </span>
                        )}
                    </div>
                );
            },
        },
        {
            accessorKey: 'Type',
            header: 'Type',
            cell: ({ row }) => {
                return <span className="text-muted-foreground text-sm">{t(`final-circuit.information.form.type.${row.original.Kind ?? 'Unknown'}`)}</span>;
            },
        },
        {
            accessorKey: 'Actions',
            header: '',
            cell: ({ row }) => (
                <div className="flex flex-row justify-end">
                    <FinalCircuitActions
                        finalCircuit={row.original}
                        electricalProjectVersionId={electricalProjectVersionId}
                    />
                </div>
            ),
        },
    ]), [electricalProjectVersionId, t]);

    const tableColumns = useMemo(
        () =>
            isLoading
                ? columns.map(column => ({
                        ...column,
                        cell: () => (
                            <Skeleton className="h-8 w-full" />
                        ),
                    }))
                : columns,
        [columns, isLoading],
    );

    if (finalCircuits.length === 0 && !isLoading) {
        return (
            <p className="text-muted-foreground text-center py-8 italic">
                {t('finalCircuits.empty')}
            </p>
        );
    }

    return (
        <DataTable
            className={cn('bg-background-primary', className)}
            columns={tableColumns}
            data={isLoading ? [{} as FinalCircuit, {} as FinalCircuit, {} as FinalCircuit] : finalCircuits}
            rowProps={row => ({
                className: 'cursor-pointer hover:bg-accent/50',
                onClick: () => {
                    if (!isLoading && row.original.ElectricalProjectFinalCircuitId) {
                        navigate({
                            to: '/projects/$projectVersionId/re-inspections/final-circuits/$finalCircuitId/information',
                            params: {
                                projectVersionId: electricalProjectVersionId,
                                finalCircuitId: row.original.ElectricalProjectFinalCircuitId,
                            },
                            search: search as Record<string, string>,
                        });
                    }
                },
            })}
            cellClassName={cell => cn({
                // Name column: flexible width, takes remaining space
                'min-w-0': cell.column.id === 'Name',
                // Actions column: minimum content width
                'w-16 min-w-16': cell.column.id === 'Actions',
            })}
        />
    );
}
